import { useEffect, useRef } from "react"
import { useLocation } from "wouter"
import { paths } from "../../../paths"

const IDLE_EVENTS = ["touchstart", "mousemove", "click", "keydown"] as const
const PROBE_TIMEOUT_MS = 3_000

// Returns the kiosk to the welcome screen after `ms` of inactivity. `onTimeout`,
// if given, runs just before navigating — e.g. the location-settings page passes
// a logout so an abandoned staff session is cleared, not just navigated away
// from. Held in a ref so callers can pass an inline callback without resetting
// the timer every render. If it returns a promise, the reload below waits for it:
// a reload aborts in-flight requests, and a logout cut off halfway leaves the
// session it was meant to clear.
//
// `enabled` is for callers that only sometimes want it: the kiosk sign-in screen
// arms it only once the device is enrolled, because returning an unenrolled tablet
// to the welcome screen would bounce off the guard and land back here.
//
// Going idle is also how a tablet takes up a new deploy. A kiosk holds one page
// for weeks — measured in production, one page load in thirty days — so nothing
// else ever fetches a new bundle. Rather than detect a deploy, the tablet simply
// re-requests the document each time it goes idle: the bundle is content-hashed
// and immutable, so when nothing has changed that costs one small request for the
// shell, and when something has, it is picked up at the very next idle.
//
// This is the only place that reloads. A participant's feedback is safe twice
// over: the pages they fill in never arm this hook, and the reload itself checks
// it is still on the welcome screen before going ahead — the first is about where
// a reload starts, the second about where it lands, and only together do they
// cover a detached chain that can resolve seconds later. The client-side
// navigation happens first either way, so a reload that cannot go ahead leaves
// the tablet on the welcome screen — exactly where it would have been. And the
// welcome screen does not arm the timer, so one idle means one reload, never a
// chain.
export function useKioskIdleTimeout(
  ms = 60_000,
  onTimeout?: () => void | Promise<void>,
  enabled = true,
) {
  const [, navigate] = useLocation()
  const onTimeoutRef = useRef(onTimeout)
  onTimeoutRef.current = onTimeout

  useEffect(() => {
    if (!enabled) return

    function fire() {
      const settled = onTimeoutRef.current?.()
      navigate(paths.kioskWelcome)
      void reloadOnceSettled(settled)
    }

    let timer = setTimeout(fire, ms)

    function reset() {
      clearTimeout(timer)
      timer = setTimeout(fire, ms)
    }

    for (const event of IDLE_EVENTS) {
      window.addEventListener(event, reset)
    }

    return () => {
      clearTimeout(timer)
      for (const event of IDLE_EVENTS) {
        window.removeEventListener(event, reset)
      }
    }
  }, [ms, navigate, enabled])
}

async function reloadOnceSettled(settled: void | Promise<void>) {
  try {
    await settled
  } catch {
    // Whatever the caller's work was, its failure is the caller's to report.
    // The reload still goes ahead: it is the safe direction.
  }
  await reloadIfReachable()
}

/**
 * Re-requests the document — but only after proving the server can be reached.
 *
 * A reload attempted while the network is down leaves the tablet on the browser's
 * error page with no script left to recover, the one outcome worse than running
 * an old build. `navigator.onLine` cannot tell: it says true behind the captive
 * portals these sites use. So the probe fetches `/health` and requires the body
 * to be ours — a portal answers 200 with its own page, and that is not `ok: true`.
 *
 * Only reloads if the tablet is still on the welcome screen. The chain is detached
 * and unbounded — an `onTimeout` like the settings page's logout is a bare fetch
 * with no timeout, and the probe adds up to three seconds — so by the time it
 * resolves a client may have walked up and tapped through to a feedback form.
 * Where the reload *starts* is structural; where it *lands* is not, and this is
 * what makes them the same thing. Picked up again means leave it alone; the next
 * idle will do it.
 *
 * `location.reload()` rather than `assign()` on purpose. Both are cross-document
 * navigations, but `assign` is an ordinary one and reaches the network only if
 * the shell's cache header says so; `reload` uses the reload cache mode and
 * revalidates regardless. The device credential is a cookie, and cookies survive
 * either, so the tablet stays enrolled.
 */
async function reloadIfReachable(): Promise<void> {
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), PROBE_TIMEOUT_MS)
  try {
    const res = await fetch("/health", {
      cache: "no-store",
      signal: abort.signal,
    })
    if (!res.ok) return
    const body: unknown = await res.json()
    if ((body as { ok?: unknown }).ok !== true) return
    if (window.location.pathname !== paths.kioskWelcome) return
    window.location.reload()
  } catch {
    // Offline, timed out, or not our server: stay put and try at the next idle.
  } finally {
    clearTimeout(timer)
  }
}
